/**
 * Typed temp files API facade over the generated contract client.
 */

import { z } from 'zod';
import { tenantTempFilesApi } from '../api/generated/tenantTempFiles.api.js';
import {
  TEMP_FILE_UPLOAD_CONCURRENCY,
  validateTempFileUploadSelection,
} from '@cnapse/shared/temp-file-upload.constants.js';
import {
  TempFileAssociateSchema,
  TempFileListQuerySchema,
  TempFileListResponseSchema,
  TempFileDetailsResponseSchema,
  TempFileAssociateResponseSchema,
  TempFileBulkDeleteResponseSchema,
  TempFileStatsResponseSchema,
  TempFileUploadResponseSchema,
  type BaseResponse,
} from '@cnapse/shared/schemas.js';

class TempFilesService {
  async listTempFiles(
    query?: z.infer<typeof TempFileListQuerySchema>,
  ): Promise<z.infer<typeof TempFileListResponseSchema>> {
    return tenantTempFilesApi.list(query ?? {}) as Promise<z.infer<typeof TempFileListResponseSchema>>;
  }

  async getTempFile(fileId: number): Promise<z.infer<typeof TempFileDetailsResponseSchema>> {
    return tenantTempFilesApi.getById(fileId) as Promise<z.infer<typeof TempFileDetailsResponseSchema>>;
  }

  async uploadTempFile(file: File): Promise<z.infer<typeof TempFileUploadResponseSchema>> {
    return tenantTempFilesApi.upload({ file }) as Promise<z.infer<typeof TempFileUploadResponseSchema>>;
  }

  async associateTempFile(
    fileId: number,
    data: z.infer<typeof TempFileAssociateSchema>,
  ): Promise<z.infer<typeof TempFileAssociateResponseSchema>> {
    return tenantTempFilesApi.associate(fileId, data) as Promise<
      z.infer<typeof TempFileAssociateResponseSchema>
    >;
  }

  async deleteTempFile(fileId: number): Promise<BaseResponse> {
    return tenantTempFilesApi.remove(fileId) as Promise<BaseResponse>;
  }

  async bulkDeleteTempFiles(
    fileIds: number[],
  ): Promise<z.infer<typeof TempFileBulkDeleteResponseSchema>> {
    return tenantTempFilesApi.bulkDelete({ fileIds }) as Promise<
      z.infer<typeof TempFileBulkDeleteResponseSchema>
    >;
  }

  async uploadTempFiles(files: File[]): Promise<{
    succeeded: number;
    failed: number;
    errors: string[];
  }> {
    const selection = validateTempFileUploadSelection(files);
    const errors = selection.rejected.map((r) => `${r.name}: ${r.reason}`);
    if (selection.selectionError) {
      errors.unshift(selection.selectionError);
    }

    if (selection.accepted.length === 0) {
      return { succeeded: 0, failed: files.length, errors };
    }

    const accepted = selection.accepted;
    let succeeded = 0;
    let failed = selection.rejected.length;

    for (let i = 0; i < accepted.length; i += TEMP_FILE_UPLOAD_CONCURRENCY) {
      const chunk = accepted.slice(i, i + TEMP_FILE_UPLOAD_CONCURRENCY);
      const results = await Promise.allSettled(chunk.map((file) => this.uploadTempFile(file)));
      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        if (result.status === 'fulfilled') {
          succeeded += 1;
        } else {
          failed += 1;
          const name = chunk[j]?.name ?? 'file';
          const message =
            result.reason instanceof Error ? result.reason.message : 'Upload failed';
          errors.push(`${name}: ${message}`);
        }
      }
    }

    return { succeeded, failed, errors };
  }

  async extendTempFileExpiration(fileId: number, additionalDays: number): Promise<BaseResponse> {
    return tenantTempFilesApi.extendExpiration(fileId, { additionalDays }) as Promise<BaseResponse>;
  }

  async getTempFileStats(): Promise<z.infer<typeof TempFileStatsResponseSchema>> {
    return tenantTempFilesApi.getStats() as Promise<z.infer<typeof TempFileStatsResponseSchema>>;
  }

  async getTempFileUploaders(): Promise<Array<{ id: number; name: string }>> {
    return tenantTempFilesApi.getUploaders() as Promise<Array<{ id: number; name: string }>>;
  }

  async downloadTempFilesZip(fileIds: number[]): Promise<Blob> {
    return tenantTempFilesApi.bulkDownload({ fileIds }) as Promise<Blob>;
  }
}

export const tempFilesService = new TempFilesService();
